import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import {
  DollarSign,
  CheckCircle,
  Clock,
  AlertTriangle,
  BarChart3,
  PieChart,
  Activity,
} from "lucide-react";
import { formatCurrency } from "./Dashboard/DentalFinancialDashboard";
import { useEffect, useState } from "react";

const monthlyData = [
  { month: "Ene", ingresos: 8500000, cobrado: 7200000, pendiente: 1300000 },
  { month: "Feb", ingresos: 6200000, cobrado: 5800000, pendiente: 400000 },
  { month: "Mar", ingresos: 11000000, cobrado: 9500000, pendiente: 1500000 },
  { month: "Abr", ingresos: 4800000, cobrado: 4200000, pendiente: 600000 },
  { month: "May", ingresos: 7300000, cobrado: 6100000, pendiente: 1200000 },
  { month: "Jun", ingresos: 9200000, cobrado: 8800000, pendiente: 400000 },
  { month: "Jul", ingresos: 5900000, cobrado: 5200000, pendiente: 700000 },
  { month: "Ago", ingresos: 8100000, cobrado: 7600000, pendiente: 500000 },
  { month: "Sep", ingresos: 10500000, cobrado: 9200000, pendiente: 1300000 },
  { month: "Oct", ingresos: 6800000, cobrado: 5900000, pendiente: 900000 },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-medium text-gray-900">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="text-sm">
            {entry.name}: {formatCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const FinancialChart = ({ setMetrics, financialData }) => {
  const [selectedChart, setSelectedChart] = useState("revenue");
  //   console.log("financialData", financialData);
  const calculateMetrics = () => {
    const totalRevenue = financialData?.reduce(
      (sum, item) => sum + item.price,
      0
    );
    const totalPaid = financialData?.reduce(
      (sum, item) => sum + (item.price - item.balance),
      0
    );
    const totalPending = financialData?.reduce(
      (sum, item) => sum + item.balance,
      0
    );
    const overduePayments = financialData?.filter(
      (item) => !item.hasBeenPaid && item.daysOverdue > 30
    );

    return {
      totalRevenue,
      totalPaid,
      totalPending,
      overdueCount: overduePayments.length,
      overdueAmount: overduePayments.reduce(
        (sum, item) => sum + item.balance,
        0
      ),
    };
  };

  const metrics = calculateMetrics();

  const paymentStatusData = [
    { name: "Pagado", value: metrics.totalPaid, color: "#10B981" },
    {
      name: "Pendiente",
      value: metrics.totalPending - metrics.overdueAmount,
      color: "#F59E0B",
    },
    { name: "Vencido", value: metrics.overdueAmount, color: "#EF4444" },
  ];

  const treatmentTypesData = [
    { name: "Extracciones", value: 6, color: "#EF4444" }, // red
    { name: "Ortodoncia", value: 4, color: "#06B6D4" }, // teal
    { name: "Caries / Rellenos", value: 8, color: "#84CC16" }, // lime green
    { name: "Limpieza Dental", value: 5, color: "#10B981" }, // emerald green
    { name: "Blanqueamiento", value: 3, color: "#8B5CF6" }, // purple
    { name: "Implantes", value: 2, color: "#3B82F6" }, // sky blue
    { name: "Endodoncia", value: 4, color: "#EC4899" }, // pink
    { name: "Prótesis", value: 2, color: "#FACC15" }, // amber
    { name: "Cirugía Maxilar", value: 1, color: "#F97316" }, // orange
    { name: "Revisión General", value: 7, color: "#64748B" }, // slate gray
  ];

  useEffect(() => {
    if (setMetrics) setMetrics(metrics);
  }, [metrics, setMetrics]);
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Análisis Financiero
            </h2>
            <p className="text-gray-600 text-sm mt-1">
              Visualización de ingresos y tendencias
            </p>
          </div>
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setSelectedChart("revenue")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                selectedChart === "revenue"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <Activity className="w-4 h-4" />
              Ingresos
            </button>
            <button
              onClick={() => setSelectedChart("status")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                selectedChart === "status"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <PieChart className="w-4 h-4" />
              Estados
            </button>
            <button
              onClick={() => setSelectedChart("treatments")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-cente truncate gap-2 ${
                selectedChart === "treatments"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              Acción
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 flex w-full sm:flex-row flex-col justify-between gap-3">
        <div className="sm:w-[70%]">
          {selectedChart === "revenue" && (
            <div className="h-40vh">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthlyData}>
                  <defs>
                    <linearGradient
                      id="colorIngresos"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient
                      id="colorCobrado"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" stroke="#6B7280" fontSize={12} />
                  <YAxis
                    stroke="#6B7280"
                    fontSize={12}
                    tickFormatter={(value) =>
                      `$${(value / 1000000).toFixed(1)}M`
                    }
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="ingresos"
                    stroke="#3B82F6"
                    fillOpacity={1}
                    fill="url(#colorIngresos)"
                    strokeWidth={3}
                    name="Ingresos"
                  />
                  <Area
                    type="monotone"
                    dataKey="cobrado"
                    stroke="#10B981"
                    fillOpacity={1}
                    fill="url(#colorCobrado)"
                    strokeWidth={3}
                    name="Cobrado"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {selectedChart === "status" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      dataKey="value"
                      data={paymentStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                    >
                      {paymentStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [formatCurrency(value), ""]}
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-4">
                {paymentStatusData.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="font-medium text-gray-900">
                        {item.name}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-900">
                        {formatCurrency(item.value)}
                      </p>
                      <p className="text-sm text-gray-500">
                        {((item.value / metrics.totalRevenue) * 100).toFixed(1)}
                        %
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedChart === "treatments" &&
            (() => {
              const maxChartHeight = 300; // max height of the chart
              const minChartHeight = 300; // min height for very short lists
              const barPadding = 20; // top + bottom margin
              const barCount = treatmentTypesData.length;

              // Calculate dynamic chart height based on number of bars
              const chartHeight = Math.min(
                maxChartHeight,
                Math.max(minChartHeight, barCount * 40) // 40px per bar as base
              );

              // Calculate bar size to fill chart proportionally
              const barSize = Math.max(
                12,
                (chartHeight - barPadding) / barCount
              );

              return (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div style={{ height: chartHeight }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={treatmentTypesData}
                        layout="vertical"
                        barCategoryGap={0}
                        margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" stroke="#6B7280" fontSize={12} />
                        <YAxis
                          dataKey="name"
                          type="category"
                          stroke="#6B7280"
                          fontSize={12}
                          width={120}
                        />
                        <Tooltip />
                        <Bar
                          dataKey="value"
                          radius={[0, 6, 6, 0]}
                          barSize={barSize}
                        >
                          {treatmentTypesData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                    {treatmentTypesData.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: item.color }}
                          />
                          <span className="font-medium text-gray-900 text-sm">
                            {item.name}
                          </span>
                        </div>
                        <span className="font-bold text-gray-900 text-sm">
                          {item.value} tratamientos
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
        </div>
        {metrics && (
          <div className="flex flex-col justify-between gap-1 h-40vh sm:w-80 w-full">
            <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <DollarSign className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-xs font-medium">
                    Ingresos Totales
                  </p>
                  <p className="text-lg font-bold text-gray-900">
                    {formatCurrency(metrics.totalRevenue)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="bg-green-100 p-2 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-xs font-medium">Cobrado</p>
                  <p className="text-lg font-bold text-green-600">
                    {formatCurrency(metrics.totalPaid)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="bg-yellow-100 p-2 rounded-lg">
                  <Clock className="w-4 h-4 text-yellow-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-xs font-medium">
                    Por Cobrar
                  </p>
                  <p className="text-lg font-bold text-yellow-600">
                    {formatCurrency(metrics.totalPending)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="bg-red-100 p-2 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-xs font-medium">Vencidos</p>
                  <div className="flex items-center justify-center gap-1">
                    <p className="text-lg font-bold text-red-600">
                      {`${metrics.overdueCount}`}
                    </p>

                    <p className="text-xs text-red-500">
                      {`(${formatCurrency(metrics.overdueAmount)})`}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancialChart;
